import React, { useState, useEffect, useRef } from "react";
import CommonTable from "../../components/commonComponent/CommanTable";
import CommonModalForm from "../../components/commonComponent/CommonModalForm";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { toastSuccess, toastError, confirmDelete } from "../../utils/alertHelper";
import {
  FaEllipsisV,
  FaEye,
  FaPencilAlt,
  FaMapMarkerAlt,
  FaFilter,
  FaPlus,
  FaTrash,
  FaDownload,
} from "react-icons/fa";
import { hasPermission } from "../../utils/permissionHelper";

function QuotationIndex() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filter, setFilter] = useState({
    search: "",
    customer: "",
    startDate: "",
    endDate: "",
  });
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [visitFormData, setVisitFormData] = useState({ user: "" });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [user, setUser] = useState(null);
  const actionMenuRef = useRef(null);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const defaultLimit = process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 10;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [totalQuotations, setTotalQuotations] = useState(0);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) setUser(JSON.parse(stored));
    } catch {}
  }, []);

  // Permission checks
  const canListQuotation = hasPermission(user, "Quatation", "List");
  const canViewQuotation = hasPermission(user, "Quatation", "View");
  const canCreateQuotation = hasPermission(user, "Quatation", "Create");
  const canEditQuotation = hasPermission(user, "Quatation", "Edit");
  const canDeleteQuotation = hasPermission(user, "Quatation", "Delete");
  // Download permission removed — allow download for all users
  const canDownloadQuotation = true;

  useEffect(() => {
    fetchQuotations(page, limit);
    fetchUsers();
    // eslint-disable-next-line
  }, [canListQuotation, canViewQuotation, page, limit]);

  const fetchQuotations = async (pageArg = page, limitArg = limit) => {
    if (!canListQuotation && !canViewQuotation) {
      setQuotations([]);
      setTotalQuotations(0);
      return;
    }
    try {
      setLoading(true);
      const businessId = localStorage.getItem("businessId");
      if (!businessId) {
        toastError("Business ID not found");
        setQuotations([]);
        setTotalQuotations(0);
        return;
      }
      const params = new URLSearchParams();
      params.append('business', businessId);
      params.append('page', pageArg);
      params.append('limit', limitArg);
      const response = await axiosInstance.get(`/api/quatation?${params.toString()}`);
      if (response.data.Status === 1) {
        const data = response.data;
        const items = data.quatations || data.data || [];
        setQuotations(items);
        const total = data.total || data.count || data.totalItems || data.totalCount || data.meta?.total;
        setTotalQuotations(Number(total) || items.length || 0);
      } else {
        toastError("Failed to fetch quotations");
        setQuotations([]);
        setTotalQuotations(0);
      }
    } catch (error) {
      toastError("Error fetching quotations");
      setQuotations([]);
      setTotalQuotations(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const businessId = localStorage.getItem("businessId");
      if (!businessId) {
        toastError("Business ID not found");
        return;
      }
      const response = await axiosInstance.get(`/api/user?business=${businessId}`);
      if (response.data.Status === 1) {
        setUsers(response.data.users || response.data.data || []);
      } else {
        toastError("Failed to fetch users");
      }
    } catch (error) {
      toastError("Error fetching users");
    } finally {
      setUsersLoading(false);
    }
  };

  const calculateQuotationTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((total, item) => {
      const rate = item.variant?.rate || 0;
      const qty = item.qty || 0;
      const gst = item.variant?.gst || 0;
      const subtotal = rate * qty;
      const gstAmount = (subtotal * gst) / 100;
      return total + subtotal + gstAmount;
    }, 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("en-IN") +
      " " +
      date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    );
  };

  const handleLeadVisit = (q) => {
    setSelectedQuotation(q);
    setVisitFormData({
      name: getLeadDisplayName(q.lead),
      visitAt: "",
      note: "",
      user: "",
    });
    setShowVisitModal(true);
  };

  const handleCloseVisitModal = () => {
    setShowVisitModal(false);
    setSelectedQuotation(null);
  };

  const handleVisitSubmit = async () => {
    try {
      if (!visitFormData.user) {
        toastError("Please select a user");
        return;
      }
      const selectedUser = users.find((user) => user._id === visitFormData.user);
      const userName =
        selectedUser?.name ||
        `${selectedUser?.firstName || ""} ${selectedUser?.lastName || ""}`.trim() ||
        selectedUser?.email ||
        "Selected User";
      toastSuccess(`Quotation assigned to ${userName} successfully`);
      setShowVisitModal(false);
    } catch (error) {
      toastError("Failed to assign quotation");
    }
  };

  const openDownloadMenu = (e, q) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDownloadMenuAnchor({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setDownloadMenuOpen(q._id);
  };

  // ✅ Updated: close dropdown immediately when clicking download
  const downloadQuotationPDF = async (q, type = "client") => {
    setDownloadMenuOpen(null);
    if (!canDownloadQuotation) {
      toastError("You do not have permission to download quotations.");
      return;
    }
    try {
      const response = await axiosInstance.get(`/api/quatation/${q._id}/export`, {
        params: { type },
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Quotation_${q.code || q._id.slice(-6)}_${type}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastSuccess("PDF downloaded successfully");
    } catch (error) {
      toastError("Failed to download PDF");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        event.target?.closest("[data-action-menu-button]") ||
        event.target?.closest("[data-download-button]")
      )
        return;
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActionMenuOpen(null);
      }
      setDownloadMenuOpen(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getLeadDisplayName = (lead) => {
    if (typeof lead === "object" && lead !== null) {
      return lead.name || `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "N/A";
    }
    return "Customer";
  };

  const handleAddNew = () => {
    if (!canCreateQuotation) {
      toastError("You do not have permission to create a quotation.");
      return;
    }
    navigate("/user/quotation/create");
  };

  const handleView = (q) => {
    if (!canViewQuotation) {
      toastError("You do not have permission to view quotations.");
      return;
    }
    navigate(`/user/quotation/view/${q._id}`);
  };

  const handleEdit = (q) => {
    if (!canEditQuotation) {
      toastError("You do not have permission to edit quotations.");
      return;
    }
    navigate(`/user/quotation/edit/${q._id}`);
  };

  const handleDelete = async (q) => {
    if (!canDeleteQuotation) {
      toastError("You do not have permission to delete quotations.");
      return;
    }
    try {
      const res = await confirmDelete(`Are you sure you want to delete quotation ${q.code || q._id.slice(-6)}?`);
      if (!res.isConfirmed) return;
    } catch (err) {
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/api/quatation/${q._id}`);
      if (response.data.Status === 1) {
        toastSuccess("Quotation deleted successfully");
        setQuotations((prev) => prev.filter((x) => x._id !== q._id));
      } else {
        toastError(response.data.Message || "Failed to delete quotation");
      }
    } catch (error) {
      toastError("Error deleting quotation");
    } finally {
      setLoading(false);
    }
  };

  const tableColumns = [
    { key: "srNo", label: "Sr.No." },
    { key: "quotationNo", label: "Quotation No" },
    { key: "dateTime", label: "Date & Time" },
    { key: "name", label: "Customer Name" },
    { key: "amount", label: "Amount" },
    { key: "actions", label: "Action" },
  ];

  const filteredQuotations = quotations.filter((q) => {
    const searchTerm = filter.search.toLowerCase();
    const leadName = getLeadDisplayName(q.lead).toLowerCase();
    const matchesSearch =
      (q.code || "").toLowerCase().includes(searchTerm) ||
      (q.title || "").toLowerCase().includes(searchTerm) ||
      leadName.includes(searchTerm);
    const matchesDateRange =
      (!filter.startDate || new Date(q.quotationDate) >= new Date(filter.startDate)) &&
      (!filter.endDate || new Date(q.quotationDate) <= new Date(filter.endDate));
    return matchesSearch && matchesDateRange;
  });

  const tableData = filteredQuotations.map((q, idx) => ({
    ...q,
    srNo: idx + 1,
    quotationNo: q.code || `QTN-${q._id.slice(-6)}`,
    dateTime: formatDate(q.quotationDate),
    name: getLeadDisplayName(q.lead),
    amount: `₹${calculateQuotationTotal(q.items).toFixed(2)}`,
    actions: (
      <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
        <button
          className="btn btn-sm me-1 py-0 px-1 shadow-none border-0 text-primary-custom"
          title="Download PDF"
          onClick={(e) => openDownloadMenu(e, q)}
          data-download-button
        >
          <FaDownload size={14} />
        </button>
        {downloadMenuOpen === q._id && (
          <div
            data-download-button
            style={{
              position: "fixed",
              zIndex: 1050,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 6,
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
              minWidth: 160,
              padding: "6px 0",
              top: downloadMenuAnchor?.top,
              left: downloadMenuAnchor?.left,
            }}
          >
            <button
              data-download-button
              className="btn btn-sm w-100 text-start py-2 px-3"
              onClick={() => downloadQuotationPDF(q, "client")}
            >
              Client PDF
            </button>
            <button
              data-download-button
              className="btn btn-sm w-100 text-start py-2 px-3"
              onClick={() => downloadQuotationPDF(q, "owner")}
            >
              Owner PDF
            </button>
          </div>
        )}
        {/* Lead Visit button can have its own permission if needed */}
        <button
          className="btn btn-sm me-1 py-0 px-1 shadow-none border-0 text-primary-custom"
          title="Lead Visit"
          onClick={() => handleLeadVisit(q)}
        >
          <FaMapMarkerAlt size={14} />
        </button>

        <button
          className="btn btn-sm py-0 px-1 shadow-none border-0 text-primary-custom"
          data-action-menu-button
          onClick={(e) => {
            e.stopPropagation();
            setActionMenuOpen(q._id);
          }}
        >
          <FaEllipsisV size={14} />
        </button>

        {actionMenuOpen === q._id && (
          <div
            ref={actionMenuRef}
            style={{
              position: "fixed",
              zIndex: 1050,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 6,
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
              minWidth: 160,
              padding: "6px 0",
            }}
          >
            {canViewQuotation && (
              <button
                className="btn btn-sm w-100 text-start py-2 px-3 lh-sm shadow-none border-0 d-flex align-items-center text-primary-custom"
                onClick={() => handleView(q)}
              >
                <FaEye size={14} className="me-2" /> View
              </button>
            )}
            {canEditQuotation && (
              <button
                className="btn btn-sm w-100 text-start py-2 px-3 lh-sm shadow-none border-0 d-flex align-items-center text-primary-custom"
                onClick={() => handleEdit(q)}
              >
                <FaPencilAlt size={14} className="me-2" /> Edit
              </button>
            )}
            <hr className="my-1" style={{ borderColor: "#eee" }} />
            {canDeleteQuotation && (
              <button
                className="btn btn-sm w-100 text-start py-2 px-3 lh-sm shadow-none border-0 d-flex align-items-center"
                onClick={() => handleDelete(q)}
                style={{ color: "#dc3545" }}
              >
                <FaTrash size={14} className="me-2 text-danger" /> Delete
              </button>
            )}
          </div>
        )}
      </div>
    ),
  }));

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold">Quotations</h2>
          <small className="text-muted">
            {loading
              ? "Loading..."
              : `${filteredQuotations.length} of ${quotations.length} quotations`}
          </small>
        </div>
        <div>
          <button
            className={`btn btn-light me-2 ${showFilter ? "active" : ""}`}
            onClick={() => setShowFilter(!showFilter)}
          >
            <FaFilter className="me-1" /> Filter
          </button>
          {canCreateQuotation && (
            <button className="btn btn-primary" onClick={handleAddNew}>
              <FaPlus className="me-1" /> Add New Quotation
            </button>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-end mb-3" style={{ gap: "10px" }}>
        <div
          className="input-group"
          style={{ maxWidth: 280, borderRadius: "6px", overflow: "hidden" }}
        >
          <span
            className="input-group-text bg-white border-end-0"
            style={{ borderRadius: "6px 0 0 6px" }}
          >
            <i className="fas fa-search text-muted"></i>
          </span>
          <input
            type="search"
            className="form-control border-start-0"
            placeholder="Search quotation..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            style={{ boxShadow: "none", borderRadius: "0 6px 6px 0" }}
          />
        </div>
      </div>

      {showFilter && (
        <div className="mb-4">
          <div className="card p-4">
            <h6 className="mb-3 fw-bold">Filter by</h6>
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label text-muted">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filter.startDate}
                  onChange={(e) => setFilter({ ...filter, startDate: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filter.endDate}
                  onChange={(e) => setFilter({ ...filter, endDate: e.target.value })}
                />
              </div>
              <div className="col-md-12 d-flex justify-content-center gap-3 mt-4">
                <button
                  className="btn btn-primary px-4"
                  onClick={() => toastSuccess("Filter applied successfully")}
                >
                  Apply Filter
                </button>
                <button
                  className="btn btn-secondary px-4"
                  onClick={() => {
                    setFilter({ search: "", customer: "", startDate: "", endDate: "" });
                    toastSuccess("Filter cleared");
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVisitModal && (
        <CommonModalForm
          visible={showVisitModal}
          title="Assign Quotation"
          onCancel={handleCloseVisitModal}
          onSubmit={handleVisitSubmit}
          values={visitFormData}
          onChange={setVisitFormData}
          fields={[
            {
              name: "user",
              label: "Select User",
              type: "select",
              placeholder: usersLoading
                ? "Loading users..."
                : users.length === 0
                ? "No users available"
                : "Select User",
              disabled: usersLoading || users.length === 0,
              options:
                users.length > 0
                  ? users.map((user) => ({
                      label:
                        user.name ||
                        `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                        user.email ||
                        "Unnamed User",
                      value: user._id || user.id,
                    }))
                  : [{ label: "No users available", value: "", disabled: true }],
            },
          ]}
        />
      )}

      <CommonTable
        columns={tableColumns}
        data={tableData}
        loading={loading}
        itemsPerPageOptions={[10, 15, 25, 50]}
        defaultItemsPerPage={10}
        page={page}
        onPageChange={setPage}
        itemsPerPage={limit}
        onItemsPerPageChange={setLimit}
      />
    </div>
  );
}

export default QuotationIndex;